import { useState, useEffect } from 'react'
import { adminApi } from '../services/api'
import {
  Building,
  Save,
  Loader2,
  AlertCircle,
  CheckCircle,
  Mail,
  Phone,
  MapPin,
  FileText
} from 'lucide-react'

export default function CompanyPage() {
  const [company, setCompany] = useState({
    name: '',
    legalName: '',
    vatNumber: '',
    fiscalCode: '',
    address: {
      street: '',
      city: '',
      province: '',
      postalCode: '',
      country: 'Italia'
    },
    email: '',
    pec: '',
    phone: '',
    sdiCode: '',
    vatRate: 22
  })
  
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState(null)
  const [success, setSuccess] = useState(null)

  useEffect(() => {
    loadSettings()
  }, [])

  const loadSettings = async () => {
    try {
      setLoading(true)
      const response = await adminApi.getSettings()
      setCompany(response.data.data.settings.company)
    } catch (err) {
      setError(err.response?.data?.message || 'Errore nel caricamento')
    } finally {
      setLoading(false)
    }
  }

  const handleSave = async () => {
    try {
      setSaving(true)
      setError(null)
      await adminApi.updateCompanySettings(company)
      setSuccess('Dati aziendali salvati con successo')
      setTimeout(() => setSuccess(null), 3000)
    } catch (err) {
      setError(err.response?.data?.message || 'Errore nel salvataggio')
    } finally {
      setSaving(false)
    }
  }

  const updateAddress = (field, value) => {
    setCompany({
      ...company,
      address: { ...company.address, [field]: value }
    })
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-primary-600" />
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Dati Aziendali</h1>
          <p className="text-gray-500">Informazioni per fatturazione e documenti legali</p>
        </div>
        <button
          onClick={handleSave}
          disabled={saving}
          className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 flex items-center"
        >
          {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
          Salva
        </button>
      </div>

      {/* Alerts */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-center">
          <AlertCircle className="h-5 w-5 text-red-500 mr-3" />
          <p className="text-red-700">{error}</p>
        </div>
      )}
      {success && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-4 flex items-center">
          <CheckCircle className="h-5 w-5 text-green-500 mr-3" />
          <p className="text-green-700">{success}</p>
        </div>
      )}

      {/* Info Box */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <h3 className="font-medium text-blue-800 mb-2">ℹ️ Quando compilare questi dati?</h3>
        <p className="text-sm text-blue-700">
          Compila questi campi quando apri la P.IVA per il lancio commerciale. 
          Questi dati verranno mostrati nel footer del sito, nelle fatture e nei documenti legali.
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Dati Fiscali */}
        <div className="bg-white rounded-xl shadow-sm p-6">
          <div className="flex items-center mb-6">
            <FileText className="h-5 w-5 text-primary-600 mr-2" />
            <h2 className="text-lg font-semibold text-gray-900">Dati Fiscali</h2>
          </div>

          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Nome Commerciale
              </label>
              <input
                type="text"
                value={company.name}
                onChange={(e) => setCompany({ ...company, name: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                placeholder="Il Diario di Bordo"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Ragione Sociale / Nome Completo *
              </label>
              <input
                type="text"
                value={company.legalName}
                onChange={(e) => setCompany({ ...company, legalName: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                placeholder="Nome Cognome (o Ragione Sociale SRL)"
              />
              <p className="mt-1 text-xs text-gray-500">Per ditta individuale: Nome e Cognome</p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Partita IVA *
              </label>
              <input
                type="text"
                value={company.vatNumber}
                onChange={(e) => setCompany({ ...company, vatNumber: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                placeholder="IT12345678901"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Codice Fiscale
              </label>
              <input
                type="text"
                value={company.fiscalCode}
                onChange={(e) => setCompany({ ...company, fiscalCode: e.target.value.toUpperCase() })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 uppercase"
                placeholder="RSSMRA80A01H501Z"
              />
              <p className="mt-1 text-xs text-gray-500">Se diverso dalla P.IVA (ditta individuale)</p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Codice SDI
              </label>
              <input
                type="text"
                value={company.sdiCode}
                onChange={(e) => setCompany({ ...company, sdiCode: e.target.value.toUpperCase() })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 uppercase"
                placeholder="XXXXXXX"
                maxLength={7}
              />
              <p className="mt-1 text-xs text-gray-500">Per fatturazione elettronica (7 caratteri)</p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Aliquota IVA %
              </label>
              <input
                type="number"
                min="0"
                max="100"
                value={company.vatRate ?? 22}
                onChange={(e) => setCompany({ ...company, vatRate: parseFloat(e.target.value) || 0 })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                placeholder="22"
              />
              <p className="mt-1 text-xs text-gray-500">Aliquota IVA applicata nelle fatture di cortesia (default: 22%)</p>
            </div>
          </div>
        </div>

        {/* Contatti */}
        <div className="bg-white rounded-xl shadow-sm p-6">
          <div className="flex items-center mb-6">
            <Mail className="h-5 w-5 text-primary-600 mr-2" />
            <h2 className="text-lg font-semibold text-gray-900">Contatti</h2>
          </div>

          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Email
              </label>
              <input
                type="email"
                value={company.email}
                onChange={(e) => setCompany({ ...company, email: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                placeholder="info@ildiariodibordo.it"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                PEC
              </label>
              <input
                type="email"
                value={company.pec}
                onChange={(e) => setCompany({ ...company, pec: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                placeholder="ildiariodibordo@pec.it"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Telefono
              </label>
              <input
                type="tel"
                value={company.phone}
                onChange={(e) => setCompany({ ...company, phone: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                placeholder="+39 123 456 7890"
              />
            </div>
          </div>
        </div>

        {/* Indirizzo */}
        <div className="bg-white rounded-xl shadow-sm p-6 lg:col-span-2">
          <div className="flex items-center mb-6">
            <MapPin className="h-5 w-5 text-primary-600 mr-2" />
            <h2 className="text-lg font-semibold text-gray-900">Sede Legale</h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Indirizzo
              </label>
              <input
                type="text"
                value={company.address.street}
                onChange={(e) => updateAddress('street', e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                placeholder="Via Roma, 1"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Città
              </label>
              <input
                type="text"
                value={company.address.city}
                onChange={(e) => updateAddress('city', e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                placeholder="Roma"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Provincia
              </label>
              <input
                type="text"
                value={company.address.province}
                onChange={(e) => updateAddress('province', e.target.value.toUpperCase())}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 uppercase"
                placeholder="RM"
                maxLength={2}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                CAP
              </label>
              <input
                type="text"
                value={company.address.postalCode}
                onChange={(e) => updateAddress('postalCode', e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                placeholder="00100"
                maxLength={5}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Paese
              </label>
              <input
                type="text"
                value={company.address.country}
                onChange={(e) => updateAddress('country', e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                placeholder="Italia"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Preview */}
      <div className="bg-white rounded-xl shadow-sm p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Anteprima Footer</h2>
        <div className="bg-gray-100 rounded-lg p-4 text-center text-sm text-gray-600">
          <p className="font-medium">{company.name || 'Il Diario di Bordo'}</p>
          {company.legalName && <p>{company.legalName}</p>}
          {company.vatNumber && <p>P.IVA: {company.vatNumber}</p>}
          {company.address.street && (
            <p>
              {company.address.street}, {company.address.postalCode} {company.address.city} ({company.address.province})
            </p>
          )}
          {company.email && <p>{company.email}</p>}
          {company.vatRate != null && <p>Aliquota IVA: {company.vatRate}%</p>}
        </div>
      </div>
    </div>
  )
}
